/**
 * audio-align.js
 * Pure vanilla JavaScript audio â text forced-alignment library.
 * Zero dependencies. Browser (Web Audio API) â no build step.
 *
 * WHAT IT DOES
 *   Given audio (base64 string OR streamed chunks) + a known transcript,
 *   produces word-level and character-level timestamps:
 *     { word: "hello", start_time: 0.00, end_time: 0.41, chars: [...] }
 *
 * HOW IT WORKS (honest description)
 *   1. Decode audio â mono PCM
 *   2. Short-time RMS energy (25ms window / 10ms hop)
 *   3. Adaptive-threshold VAD with hysteresis â voiced segments
 *   4. Transcript words are assigned duration weights based on
 *      phonetic heuristics (vowels sustain, plosives are short, etc.)
 *   5. Words are distributed across voiced time proportionally,
 *      with boundaries snapped to local energy minima (valleys)
 *   6. Characters subdivide each word span by per-character weights
 *
 *   This is heuristic forced alignment â typical accuracy Â±30â80ms.
 *   Great for lip-sync, karaoke highlighting, caption timing.
 *   It does NOT do speech recognition: the transcript must be supplied.
 *
 * USAGE
 *   const aligner = new AudioAligner();
 *   const result = await aligner.alignBase64(b64Audio, "hello world");
 *   // result.words[0] => { word:"hello", start_time:0.02, end_time:0.38, chars:[...] }
 *
 *   // Streaming:
 *   const s = aligner.createStream({ sampleRate: 16000 });
 *   s.push(float32Chunk);           // or s.pushBase64PCM(b64chunk)
 *   const result = s.finalize("hello world");
 */

(function (global) {

  /* ============================== config ============================== */

  const DEFAULTS = {
    frameMs: 25,          // analysis window
    hopMs: 10,            // hop between frames
    minSegmentMs: 60,     // drop voiced blips shorter than this
    mergeGapMs: 140,      // merge voiced segments separated by less
    energyFloorPct: 0.15, // percentile used as noise floor estimate
    onRatio: 2.8,         // enter "voiced" when energy > floor * onRatio
    offRatio: 1.6,        // leave "voiced" when energy < floor * offRatio (hysteresis)
    padMs: 15,            // pad each segment edges slightly
  };

  /* ==================== phonetic duration heuristics =================== */
  // Relative time-cost of characters when spoken. Rough but effective:
  // vowels & sonorants sustain, stops/plosives are brief, silent-ish
  // punctuation costs ~0 but introduces pause weight between words.

  const CHAR_WEIGHTS = {
    a: 1.0, e: 1.0, i: 0.95, o: 1.0, u: 0.95, y: 0.8,
    m: 0.75, n: 0.75, l: 0.8, r: 0.8, w: 0.7,
    s: 0.7, z: 0.7, f: 0.65, v: 0.65, h: 0.5,
    j: 0.6, g: 0.55, c: 0.55, k: 0.5, q: 0.5,
    b: 0.5, d: 0.5, p: 0.5, t: 0.5, x: 0.7,
    // Arabic vowels/letters get a flat sensible default below
  };
  const DEFAULT_CHAR_WEIGHT = 0.7;
  // Arabic per-letter weights. Unvocalized Arabic omits short vowels that ARE
  // spoken â each consonant typically carries an implicit vowel (CV syllable),
  // so Arabic consonants weigh ~0.8â0.95 vs English consonants at 0.5â0.7.
  // Without this, Arabic words are underweighted in mixed AR+EN sentences and
  // English words steal time from their Arabic neighbors.
  const ARABIC_WEIGHTS = {
    "ا": 1.0, "آ": 1.1, "أ": 0.9, "إ": 0.9, "و": 1.0, "ي": 1.0, "ى": 1.0,
    "ء": 0.4, "ؤ": 0.6, "ئ": 0.6,
    "ب": 0.85, "ت": 0.8, "ث": 0.85, "ج": 0.9, "ح": 0.9, "خ": 0.9,
    "د": 0.8, "ذ": 0.85, "ر": 0.9, "ز": 0.85, "س": 0.9, "ش": 0.95,
    "ص": 0.95, "ض": 0.95, "ط": 0.9, "ظ": 0.95, "ع": 0.95, "غ": 0.9,
    "ف": 0.85, "ق": 0.85, "ك": 0.8, "ل": 0.85, "م": 0.85, "ن": 0.85,
    "ه": 0.8, "ة": 0.5,
  };
  const SHADDA = "ّ"; // gemination — doubles the previous consonant

  function charWeight(ch) {
    const lower = ch.toLowerCase();
    if (CHAR_WEIGHTS[lower] != null) return CHAR_WEIGHTS[lower];
    if (ARABIC_WEIGHTS[ch] != null) return ARABIC_WEIGHTS[ch];
    if (ch === SHADDA) return 0.5;               // gemination adds real duration
    if (/[ً-ِْ]/.test(ch)) return 0.1; // other harakat: tiny
    if (/\d/.test(ch)) return 0.9;               // digits read as words-ish
    if (/[^\p{L}\p{N}]/u.test(ch)) return 0.0;   // punctuation: no audio time
    return DEFAULT_CHAR_WEIGHT;
  }

  // Pause weight injected AFTER a word ending with this punctuation
  function trailingPauseWeight(word) {
    if (/[.!?؟]$/.test(word)) return 1.6;
    if (/[,;:،؛…—–-]$/.test(word)) return 0.8;
    return 0.25; // natural inter-word gap
  }

  function _hasSpokenContent(word) {
    return /[\p{L}\p{N}]/u.test(word);
  }

  // Numerals are read as expanded words: "84" â "Ø£Ø±Ø¨Ø¹Ø© ÙØ«ÙØ§ÙÙÙ" (~5 syllables),
  // "500" â "Ø®ÙØ³ÙØ§Ø¦Ø©", "6" â "Ø³ØªØ©". Weighting them per digit character
  // starves them of time and makes neighbors rush. Estimate spoken length:
  // ~3.0 per nonzero digit (each becomes a number word), +0.8 per compound
  // join ("Ù"/"and"), +1.2 when a hundreds part is present (ÙØ§Ø¦Ø©/hundred).
  function _numberWeight(numStr) {
    const n = numStr.replace(/^0+/, "") || "0";
    let nonzero = 0;
    for (const d of n) if (d !== "0") nonzero++;
    nonzero = Math.max(nonzero, 1);
    let w = 3.0 * nonzero + 0.8 * (nonzero - 1);
    if (n.length >= 3) w += 2.2;                 // hundreds word (Ø®ÙØ³ÙØ§Ø¦Ø© is fused, ~4 syll)
    if (n.length >= 4) w += 1.2 * Math.floor((n.length - 1) / 3); // Ø£ÙÙ/ÙÙÙÙÙâ¦
    return w;
  }

  // Arabic clitic prefixes (Ù Ù Ø¨ Ù ÙØ Ø§ÙØ ÙÙ) attach to the written token
  // but are spoken fast and unstressed. Splitting them internally (weights
  // only â output text is untouched) stops "ÙØªÙØ¯Ø±"/"ÙÙÙÙØ¸ÙÙÙ" from being
  // priced like fully-stressed content of that length.
  const AR_SINGLE_CLITICS = new Set(["و", "ف", "ب", "ك", "ل"]);
  function _cliticUnits(word) {
    let w = word, units = [], guard = 0;
    while (guard++ < 3 && w.length > 3) {
      if ((w.startsWith("ال") || w.startsWith("لل")) && w.length > 4) {
        units.push(w.slice(0, 2)); w = w.slice(2); break;
      }
      if (AR_SINGLE_CLITICS.has(w[0])) { units.push(w[0]); w = w.slice(1); continue; }
      break;
    }
    units.push(w);
    return units;
  }

  function wordWeight(word) {
    // Pure punctuation tokens (standalone â â ... etc.) take no speech time
    if (!_hasSpokenContent(word)) return 0.02;
    const stripped = word.replace(/[^\p{L}\p{N}]/gu, "");
    // Numeric tokens: weight by spoken expansion, not digit count
    if (/^\d+$/.test(stripped)) return _numberWeight(stripped);
    // Latin acronyms are spoken letter-by-letter ("EAD" â "ee-ay-dee"),
    // and each letter NAME is a full syllable, usually with a long vowel â
    // so each letter costs a syllable's worth (~1.8), not a phoneme's
    if (/^[A-Z0-9]{2,6}$/.test(stripped)) return stripped.length * 1.8;
    // Arabic tokens: price clitic prefixes at reduced (unstressed) cost
    if (/[؀-ۿ]/.test(stripped)) {
      const units = _cliticUnits(stripped);
      let w = 0;
      units.forEach((u, i) => {
        let uw = 0;
        for (const ch of u) uw += charWeight(ch);
        w += i < units.length - 1 ? uw * _CFG.cliticFactor : uw; // clitics spoken fast
      });
      return Math.max(w, 0.2);
    }
    let w = 0;
    for (const ch of word) w += charWeight(ch);
    return Math.max(w, 0.2);
  }

  // Function words are spoken compressed/reduced in natural speech â
  // giving them their full letter-weight share over-allocates them and
  // steals time from neighboring content words.
  const FUNCTION_WORDS = new Set([
    // English
    "a", "an", "the", "of", "to", "in", "on", "at", "is", "are", "was",
    "were", "and", "or", "for", "it", "its", "be", "as", "by", "we", "he",
    // Arabic
    "من", "في", "إلى", "على", "عن", "أن", "إن", "و", "ما", "لا", "يا",
    "هو", "هي", "ثم", "أو", "قد", "هذا", "هذه", "التي", "الذي", "ال",
    "أنا", "وأن", "بأن", "لكن", "بعض", "مع", "بين", "عند", "منذ", "حتى",
  ]);

  function _isFunctionWord(word) {
    return FUNCTION_WORDS.has(word.toLowerCase().replace(/[^\p{L}]/gu, ""));
  }

  // Minimum plausible spoken duration for a word â prevents flash-words.
  function _minWordDur(word) {
    const stripped = word.replace(/[^\p{L}\p{N}]/gu, "");
    if (/^\d+$/.test(stripped)) {
      // numerals: size the floor by spoken expansion, not digit count
      return Math.min(0.35, 0.06 + 0.028 * _numberWeight(stripped));
    }
    let visible = 0;
    for (const ch of word) if (/[\p{L}\p{N}]/u.test(ch)) visible++;
    if (visible === 0) return 0; // punctuation-only token: no floor
    return Math.min(0.35, 0.06 + 0.028 * visible);
  }

  // mutable per-align tuning shared with module-level weight fns
  const _CFG = { cliticFactor: 1.0, snapDepthRatio: 1.5 };

  /* ============================ core class ============================ */

  class AudioAligner {
    constructor(options = {}) {
      this.opts = Object.assign({}, DEFAULTS, options);
      this._ctx = null;
    }

    /* ---------- public API ---------- */

    /**
     * Align a full base64 audio string against a transcript.
     * @param {string} base64  data-URI or raw base64 of wav/mp3/ogg/etc.
     * @param {string} transcript  the known spoken text
     * @returns {Promise<AlignmentResult>}
     */
    async alignBase64(base64, transcript) {
      const { pcm, sampleRate } = await this._decodeBase64(base64);
      return this._align(pcm, sampleRate, transcript);
    }

    /**
     * Align an already-decoded AudioBuffer.
     */
    alignAudioBuffer(audioBuffer, transcript) {
      const pcm = this._downmix(audioBuffer);
      return this._align(pcm, audioBuffer.sampleRate, transcript);
    }

    /**
     * Align raw mono Float32 PCM.
     */
    alignPCM(float32, sampleRate, transcript) {
      return this._align(float32, sampleRate, transcript);
    }

    /**
     * Create a streaming session. Push chunks as they arrive, then finalize.
     * Chunks: Float32Array of mono PCM, or base64 of raw 16-bit PCM LE.
     */
    createStream({ sampleRate = 16000 } = {}) {
      const self = this;
      const chunks = [];
      let total = 0;
      return {
        push(float32) {
          chunks.push(float32);
          total += float32.length;
        },
        pushBase64PCM(b64) {
          const bytes = _b64ToBytes(b64);
          const i16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength >> 1);
          const f32 = new Float32Array(i16.length);
          for (let i = 0; i < i16.length; i++) f32[i] = i16[i] / 32768;
          this.push(f32);
        },
        /** Partial VAD view without a transcript (segments only). */
        segments() {
          const pcm = _concat(chunks, total);
          return self._voicedSegments(pcm, sampleRate);
        },
        /** Full alignment once you know the transcript (or per sentence). */
        finalize(transcript) {
          const pcm = _concat(chunks, total);
          return self._align(pcm, sampleRate, transcript);
        },
      };
    }

    /**
     * Align base64 of RAW PCM (headerless â no WAV/MP3 container).
     * This is what TTS endpoints emit with output formats like
     * pcm_16000 / pcm_24000, and what most self-hosted TTS streams produce.
     * @param {string} base64
     * @param {object} opts { sampleRate: 24000, encoding: 'pcm16'|'float32'|'ulaw' }
     * @param {string} transcript
     */
    alignRawPCMBase64(base64, { sampleRate, encoding = "pcm16" }, transcript) {
      if (!sampleRate) throw new Error("Raw PCM requires a sampleRate (e.g. 16000, 22050, 24000, 44100).");
      const pcm = AudioAligner.decodeRawPCM(_b64ToBytes(base64.includes(",") ? base64.split(",")[1] : base64), encoding);
      return this._align(pcm, sampleRate, transcript);
    }

    /** Decode raw PCM bytes â mono Float32Array. */
    static decodeRawPCM(bytes, encoding = "pcm16") {
      if (encoding === "float32") {
        return new Float32Array(bytes.buffer, bytes.byteOffset, bytes.byteLength >> 2);
      }
      if (encoding === "ulaw") {
        const out = new Float32Array(bytes.length);
        for (let i = 0; i < bytes.length; i++) out[i] = _ulawToLinear(bytes[i]) / 32768;
        return out;
      }
      // default: 16-bit signed little-endian PCM
      const i16 = new Int16Array(bytes.buffer, bytes.byteOffset, bytes.byteLength >> 1);
      const out = new Float32Array(i16.length);
      for (let i = 0; i < i16.length; i++) out[i] = i16[i] / 32768;
      return out;
    }

    /**
     * Sniff container format from leading bytes.
     * Returns a format name, or null if no known header (likely raw PCM).
     */
    static sniffContainer(bytes) {
      const s = String.fromCharCode(...bytes.slice(0, 12));
      if (s.startsWith("RIFF")) return "wav";
      if (s.startsWith("ID3") || (bytes[0] === 0xFF && (bytes[1] & 0xE0) === 0xE0)) return "mp3";
      if (s.startsWith("OggS")) return "ogg";
      if (s.slice(4, 8) === "ftyp") return "mp4/m4a";
      if (bytes[0] === 0x1A && bytes[1] === 0x45 && bytes[2] === 0xDF && bytes[3] === 0xA3) return "webm";
      if (s.startsWith("fLaC")) return "flac";
      return null;
    }

    /* ---------- decoding ---------- */

    async _decodeBase64(base64) {
      const raw = base64.includes(",") ? base64.split(",")[1] : base64;
      const bytes = _b64ToBytes(raw);
      const AC = global.AudioContext || global.webkitAudioContext;
      if (!AC) throw new Error("Web Audio API not available in this environment.");
      if (!this._ctx) this._ctx = new AC();
      const audioBuffer = await this._ctx.decodeAudioData(bytes.buffer.slice(0));
      return { pcm: this._downmix(audioBuffer), sampleRate: audioBuffer.sampleRate };
    }

    _downmix(audioBuffer) {
      const ch = audioBuffer.numberOfChannels;
      if (ch === 1) return audioBuffer.getChannelData(0);
      const len = audioBuffer.length;
      const out = new Float32Array(len);
      for (let c = 0; c < ch; c++) {
        const data = audioBuffer.getChannelData(c);
        for (let i = 0; i < len; i++) out[i] += data[i] / ch;
      }
      return out;
    }

    /* ---------- energy + VAD ---------- */

    _energyFrames(pcm, sampleRate) {
      const win = Math.round((this.opts.frameMs / 1000) * sampleRate);
      const hop = Math.round((this.opts.hopMs / 1000) * sampleRate);
      const n = Math.max(0, Math.floor((pcm.length - win) / hop) + 1);
      const energies = new Float32Array(n);
      const zcrs = new Float32Array(n);
      for (let f = 0; f < n; f++) {
        const start = f * hop;
        let sum = 0, crossings = 0;
        for (let i = 0; i < win; i++) {
          const s = pcm[start + i];
          sum += s * s;
          if (i > 0 && (s >= 0) !== (pcm[start + i - 1] >= 0)) crossings++;
        }
        energies[f] = Math.sqrt(sum / win); // RMS
        zcrs[f] = crossings / win;          // normalized zero-crossing rate
      }
      return { energies, zcrs, hop, win };
    }

    _voicedSegments(pcm, sampleRate) {
      const { energies, zcrs, hop } = this._energyFrames(pcm, sampleRate);
      if (energies.length === 0) return [];

      // Adaptive noise floor: low percentile of frame energies
      const sorted = Float32Array.from(energies).sort();
      const floor = Math.max(
        sorted[Math.floor(sorted.length * this.opts.energyFloorPct)],
        1e-5
      );
      const onTh = floor * this.opts.onRatio;
      const offTh = floor * this.opts.offRatio;
      // Fricative rescue: moderate energy + high ZCR also counts as speech.
      // Sounds like /s/ /f/ /sh/ (Arabic Ø³ Ø´ Ù Ø­ Ø® Ø«) carry little energy
      // but lots of high-frequency content â pure RMS VAD clips them.
      const fricEnergyTh = floor * 1.3;
      const fricZcrTh = this.opts.fricZcrTh ?? 0.12;

      const isSpeech = f =>
        energies[f] > onTh ||
        (energies[f] > fricEnergyTh && zcrs[f] > fricZcrTh);
      const isStillSpeech = f =>
        energies[f] > offTh ||
        (energies[f] > fricEnergyTh * 0.8 && zcrs[f] > fricZcrTh);

      const frameSec = hop / sampleRate;
      const segs = [];
      let inSeg = false, segStart = 0;

      for (let f = 0; f < energies.length; f++) {
        if (!inSeg && isSpeech(f)) { inSeg = true; segStart = f; }
        else if (inSeg && !isStillSpeech(f)) {
          segs.push([segStart * frameSec, f * frameSec]);
          inSeg = false;
        }
      }
      if (inSeg) segs.push([segStart * frameSec, energies.length * frameSec]);

      // merge close segments, drop tiny blips, pad
      const mergeGap = this.opts.mergeGapMs / 1000;
      const minSeg = this.opts.minSegmentMs / 1000;
      const pad = this.opts.padMs / 1000;
      const merged = [];
      for (const s of segs) {
        const last = merged[merged.length - 1];
        if (last && s[0] - last[1] < mergeGap) last[1] = s[1];
        else merged.push(s.slice());
      }
      const dur = pcm.length / sampleRate;
      return merged
        .filter(s => s[1] - s[0] >= minSeg)
        .map(s => [Math.max(0, s[0] - pad), Math.min(dur, s[1] + pad)]);
    }

    /* ---------- alignment ---------- */

    _align(pcm, sampleRate, transcript) {
      if (!transcript || !transcript.trim()) {
        throw new Error(
          "AudioAligner needs the transcript (this library aligns known text " +
          "to audio; it does not perform speech recognition)."
        );
      }
      const duration = pcm.length / sampleRate;
      const segments = this._voicedSegments(pcm, sampleRate);
      const { energies, hop } = this._energyFrames(pcm, sampleRate);
      const frameSec = hop / sampleRate;

      const words = transcript.trim().split(/\s+/);
      const voiced = segments.length ? segments : [[0, duration]];
      const weights = words.map(wordWeight);
      this._dbg = this.opts.debug ? { predicted: [], clamps: 0, snapAccepted: 0, snapTried: 0 } : null;
      _CFG.cliticFactor = this.opts.cliticFactor ?? 1.0;  // grid-fitted: compression rejected by eval data
      _CFG.snapDepthRatio = this.opts.snapDepthRatio ?? 1.5; // grid-fitted: acceptance test neutral on eval data

      // ---- Segment partitioning (dynamic programming) ----
      // VAD pauses ARE word boundaries. Partition the word sequence into
      // one contiguous group per voiced segment, minimizing the squared
      // mismatch between each group's weight fraction and its segment's
      // duration fraction. Falls back to a single group when 1 segment.
      // per-segment energy mass (second observable besides duration)
      const segEnergyFrac = (() => {
        const es = voiced.map(([s, e]) => {
          let sum = 0;
          for (let f = Math.round(s / frameSec); f < Math.round(e / frameSec) && f < energies.length; f++) sum += energies[f];
          return sum;
        });
        const tot = es.reduce((a, b) => a + b, 0) || 1;
        return es.map(x => x / tot);
      })();
      const groups = _partitionWordsToSegments(weights, voiced, words, this.opts, segEnergyFrac);

      const out = [];
      for (let g = 0; g < groups.length; g++) {
        const [wStart, wEnd] = groups[g];          // word index range [incl, excl)
        const [segS, segE] = voiced[g];
        if (wEnd <= wStart) continue;
        const gWords = words.slice(wStart, wEnd);
        const n = gWords.length;
        const segDur = segE - segS;

        // Prosody-adjusted weights:
        // - function words are spoken compressed (Ã0.8)
        // - phrase-final lengthening: last word before a pause stretches (Ã1.25)
        const P_INIT = this.opts.initialLengthen ?? 1.0;
        const adjWeights = gWords.map((w, i) => {
          let v = wordWeight(w);
          if (_isFunctionWord(w)) v *= 0.8;
          if (i === n - 1) v *= 1.25;      // phrase-final lengthening
          if (i === 0) v *= P_INIT;        // segment-initial lengthening
          return v;
        });
        const pauses = gWords.map((w, i) =>
          i < n - 1 ? trailingPauseWeight(w) * 0.15 : 0);
        const gTotal = adjWeights.reduce((a, b) => a + b, 0) +
                       pauses.reduce((a, b) => a + b, 0);

        // Breath-onset trim: after long pauses the VAD often captures an
        // in-breath as speech onset. Advance the first word's start past the
        // low-energy lead-in (below onsetTrimRatio Ã segment median energy,
        // capped at 350ms) so word 1 begins where it's actually audible.
        let allocStart = segS;
        const P_TRIM = this.opts.onsetTrimRatio ?? 0.35;
        if (P_TRIM > 0) {
          const f0 = Math.round(segS / frameSec), f1 = Math.round(segE / frameSec);
          const segEs = [];
          for (let f = f0; f < Math.min(f1, energies.length); f++) segEs.push(energies[f]);
          const medE = segEs.slice().sort((a, b2) => a - b2)[segEs.length >> 1] || 0;
          const maxTrimF = f0 + Math.round(0.35 / frameSec);
          let f = f0;
          while (f < Math.min(f1, maxTrimF, energies.length) && energies[f] < P_TRIM * medE) f++;
          allocStart = f * frameSec;
        }
        // Predicted boundaries b[0..n], b[0]=allocStart, b[n]=segE
        const b = new Float64Array(n + 1);
        b[0] = allocStart;
        const allocDur = segE - allocStart;
        let cursor = allocStart;
        for (let i = 0; i < n; i++) {
          cursor += (adjWeights[i] / gTotal) * allocDur;
          b[i + 1] = Math.min(cursor, segE);
          cursor += (pauses[i] / gTotal) * segDur;
        }
        b[n] = segE;

        // Snap internal boundaries to a nearby energy valley â but only if
        // the valley passes the significance test; else trust the model.
        const snapFlags = new Array(n + 1).fill(false);
        for (let i = 1; i < n; i++) {
          const r = _snapToValleyWeighted(b[i], energies, frameSec, 0.12, 0.35);
          snapFlags[i] = r.snapped;
          b[i] = Math.max(segS, Math.min(r.t, segE));
        }
        if (this._dbg) this._dbg.predicted.push(...Array.from(b).map(x => _r(x)));

        // Enforce per-word minimum durations (two-pass clamp).
        // Prevents "flash words" that the highlight skips through.
        const mins = gWords.map(_minWordDur);
        const minSum = mins.reduce((a, c) => a + c, 0);
        const scale = minSum > (segE - allocStart) ? (segE - allocStart) / minSum : 1;
        let clampCount = 0;
        for (let i = 1; i <= n; i++) {
          const lo2 = b[i - 1] + mins[i - 1] * scale;
          if (b[i] < lo2) { b[i] = lo2; clampCount++; }
        }
        b[n] = segE;
        for (let i = n - 1; i >= 1; i--) {
          const hi2 = b[i + 1] - mins[i] * scale;
          if (b[i] > hi2) { b[i] = hi2; clampCount++; }
        }
        if (this._dbg) { this._dbg.clamps += clampCount; this._dbg.snapAccepted += snapFlags.filter(Boolean).length; this._dbg.snapTried += Math.max(0, n - 1); }

        for (let i = 0; i < n; i++) {
          const floor = _hasSpokenContent(gWords[i]) ? 0.02 : 0;
          let start = b[i], end = Math.max(b[i + 1], b[i] + floor);
          const entry = _buildWordEntry(gWords[i], start, end);
          // confidence: acoustic anchors (segment edges, accepted snaps) are
          // trustworthy; pure model predictions less so
          let conf = 0.45;
          if (i === 0) conf += 0.3; else if (snapFlags[i]) conf += 0.25;
          if (i === n - 1) conf += 0.25; else if (snapFlags[i + 1]) conf += 0.2;
          entry.confidence = Math.min(1, +conf.toFixed(2));
          if (!_hasSpokenContent(gWords[i])) entry.confidence = 1;
          out.push(entry);
        }
      }

      const result = {
        duration: _r(duration),
        sampleRate,
        voiced_segments: voiced.map(s => ({ start_time: _r(s[0]), end_time: _r(s[1]) })),
        words: out,
      };
      const el = AudioAligner.toElevenLabs(result, transcript);
      result.alignment = el.alignment;
      result.normalized_alignment = el.normalized_alignment;

      // ---- Sanity warnings: catch transcript/audio mismatches ----
      result.warnings = [];
      const spoken = words.filter(_hasSpokenContent);
      const voicedTotal = voiced.reduce((a, s) => a + (s[1] - s[0]), 0);
      const wps = spoken.length / Math.max(voicedTotal, 0.001);
      if (wps > 3.6) {
        result.warnings.push(
          "Transcript has " + spoken.length + " words for " +
          voicedTotal.toFixed(1) + "s of speech (" + wps.toFixed(1) +
          " words/sec) â implausibly fast. The transcript may not match " +
          "this audio, or may have been pasted more than once."
        );
      } else if (wps < 0.8) {
        result.warnings.push(
          "Only " + wps.toFixed(1) + " words/sec of speech â the transcript " +
          "may be incomplete for this audio."
        );
      }
      if (words.length >= 6 && words.length % 2 === 0) {
        const half = words.length / 2;
        if (words.slice(0, half).join(" ") === words.slice(half).join(" ")) {
          result.warnings.push(
            "The transcript's second half is an exact repeat of its first " +
            "half â it was likely pasted twice."
          );
        }
      }
      // Structural quality warnings + optional debug
      if (this._dbg) {
        const d = this._dbg;
        const snapRate = d.snapTried ? d.snapAccepted / d.snapTried : 1;
        if (snapRate < 0.4 && d.snapTried >= 5) {
          result.warnings.push(
            "Only " + Math.round(snapRate * 100) + "% of word boundaries found " +
            "acoustic support (energy valleys) â heavily blended speech; " +
            "interior boundaries rely mostly on the duration model."
          );
        }
        if (d.clamps > words.length * 0.4) {
          result.warnings.push(
            d.clamps + " minimum-duration clamps applied â the duration model " +
            "disagrees strongly with segment lengths; check the transcript matches."
          );
        }
        result.debug = {
          weights: words.map((w, i) => ({ word: w, weight: +weights[i].toFixed(2) })),
          snap_accept_rate: +((d.snapTried ? d.snapAccepted / d.snapTried : 1)).toFixed(2),
          min_duration_clamps: d.clamps,
          boundaries_predicted: d.predicted,
        };
        this._dbg = null;
      }
      return result;
    }

    /**
     * Convert an alignment result into the ElevenLabs timestamps schema:
     *   { alignment: { characters: [...],
     *                  character_start_times_seconds: [...],
     *                  character_end_times_seconds: [...] },
     *     normalized_alignment: { ...same shape... } }
     *
     * - `alignment` preserves the ORIGINAL text exactly, including its
     *   whitespace. Whitespace characters are timed as the gap between
     *   the surrounding words (like ElevenLabs does).
     * - `normalized_alignment` uses whitespace-collapsed, lowercased text.
     */
    static toElevenLabs(result, originalText) {
      const buildFrom = (text, words) => {
        const characters = [];
        const starts = [];
        const ends = [];
        let wi = 0;      // word index
        let ci = 0;      // char index inside current word
        let cursor = 0;  // position in text
        const chars = [...text];

        while (cursor < chars.length) {
          const ch = chars[cursor];
          if (/\s/.test(ch)) {
            // Whitespace: spans from previous word end to next word start
            const prevEnd = wi > 0 ? words[wi - 1].end_time
                                   : (words[0] ? words[0].start_time : 0);
            const nextStart = wi < words.length ? words[wi].start_time : prevEnd;
            characters.push(ch);
            starts.push(prevEnd);
            ends.push(nextStart);
            cursor++;
            continue;
          }
          const word = words[wi];
          if (!word) { // more text than words (shouldn't happen) â clamp
            characters.push(ch);
            starts.push(result.duration);
            ends.push(result.duration);
            cursor++;
            continue;
          }
          const c = word.chars[ci];
          characters.push(ch);
          starts.push(c ? c.start_time : word.start_time);
          ends.push(c ? c.end_time : word.end_time);
          ci++;
          cursor++;
          if (ci >= word.chars.length) { wi++; ci = 0; }
        }
        return {
          characters,
          character_start_times_seconds: starts,
          character_end_times_seconds: ends,
        };
      };

      // alignment: original text as-is
      const alignment = buildFrom(originalText, result.words);

      // normalized_alignment: collapsed whitespace + lowercase.
      // Word timings are identical; only the text representation changes.
      const normText = originalText.trim().split(/\s+/).join(" ").toLowerCase();
      const normWords = result.words.map(w => ({
        ...w,
        word: w.word.toLowerCase(),
        chars: w.chars.map(c => ({ ...c, char: c.char.toLowerCase() })),
      }));
      const normalized_alignment = buildFrom(normText, normWords);

      return { alignment, normalized_alignment };
    }
  }

  /* ============================ helpers ============================ */

  function _b64ToBytes(b64) {
    const bin = global.atob(b64);
    const bytes = new Uint8Array(bin.length);
    for (let i = 0; i < bin.length; i++) bytes[i] = bin.charCodeAt(i);
    return bytes;
  }

  function _concat(chunks, total) {
    const out = new Float32Array(total);
    let off = 0;
    for (const c of chunks) { out.set(c, off); off += c.length; }
    return out;
  }

  // Maps voiced-timeline seconds â absolute seconds across segment gaps.
  // eslint-disable-next-line no-unused-vars
  function _makeVoicedMapper(segments) {
    const cum = [];
    let acc = 0;
    for (const [s, e] of segments) { cum.push({ s, e, vStart: acc }); acc += e - s; }
    const total = acc;
    return function (v) {
      if (v <= 0) return segments[0][0];
      if (v >= total) return segments[segments.length - 1][1];
      for (const seg of cum) {
        const len = seg.e - seg.s;
        if (v <= seg.vStart + len) return seg.s + (v - seg.vStart);
      }
      return segments[segments.length - 1][1];
    };
  }

  // If t is in a gap between voiced segments, move it to the nearest
  // segment edge in the given direction ("forward" â next segment start,
  // "backward" â previous segment end). If t is inside a segment, no-op.
  // eslint-disable-next-line no-unused-vars
  function _clampIntoVoiced(t, segments, direction) {
    for (const [s, e] of segments) if (t >= s && t <= e) return t;
    if (direction === "forward") {
      for (const [s] of segments) if (s >= t) return s;
      return segments[segments.length - 1][1];
    } else {
      for (let i = segments.length - 1; i >= 0; i--)
        if (segments[i][1] <= t) return segments[i][1];
      return segments[0][0];
    }
  }

  // Weighted valley search with ACCEPTANCE TEST: the best candidate must be
  // meaningfully deeper than the local median energy (a real inter-word dip)
  // and within maxShift of the prediction. Blended speech (common in Arabic)
  // often has NO true valley at a word boundary â snapping to a fake one is
  // worse than trusting the duration model. Returns { t, snapped }.
  function _snapToValleyWeighted(t, energies, frameSec, windowSec, lambda) {
    const center = Math.round(t / frameSec);
    const radius = Math.max(1, Math.round(windowSec / frameSec));
    const lo = Math.max(0, center - radius);
    const hi = Math.min(energies.length - 1, center + radius);
    const win = [];
    let maxE = 1e-9;
    for (let f = lo; f <= hi; f++) { win.push(energies[f]); if (energies[f] > maxE) maxE = energies[f]; }
    const median = win.slice().sort((a, b) => a - b)[win.length >> 1] || 1e-9;
    let best = center, bestScore = Infinity;
    for (let f = lo; f <= hi; f++) {
      const score = energies[f] / maxE + lambda * Math.abs(f - center) / radius;
      if (score < bestScore) { bestScore = score; best = f; }
    }
    // acceptance: real dip vs local median â else keep prediction
    if (energies[best] > _CFG.snapDepthRatio * median) return { t, snapped: false };
    return { t: best * frameSec, snapped: true };
  }

  // Find local RMS minimum within Â±window seconds of t.
  // eslint-disable-next-line no-unused-vars
  function _snapToValley(t, energies, frameSec, windowSec) {
    const center = Math.round(t / frameSec);
    const radius = Math.round(windowSec / frameSec);
    let best = center, bestE = Infinity;
    for (let f = Math.max(0, center - radius); f <= Math.min(energies.length - 1, center + radius); f++) {
      if (energies[f] < bestE) { bestE = energies[f]; best = f; }
    }
    return best * frameSec;
  }

  // Partition W words into S contiguous groups (one per voiced segment),
  // minimizing Î£ (groupWeightFraction â segmentDurationFraction)Â²,
  // with a bonus for placing group breaks right after pause punctuation â
  // speakers pause at sentence enders and commas, so VAD gaps and
  // punctuation should coincide.
  function _partitionWordsToSegments(weights, segments, words, opts = {}, segEnergyFrac = null) {
    // Defaults fitted by grid search against human-tapped ground truth
    // (74 labeled word onsets, Saudi banking TTS) plus the EAD constraints.
    const P_LAMBDA = opts.dpLambda ?? 0.012;         // prosody prior strength
    const P_FUNC = opts.dpFuncPenalty ?? 0;          // function-word break penalty (Ã LAMBDA)
    const P_DEF = opts.dpDefaultStrength ?? 0.02;    // default break strength
    const P_EBLEND = opts.dpEnergyBlend ?? 0.35;     // duration/energy blend for segment matching
    const W = weights.length, S = segments.length;
    if (S <= 1 || W <= 1) return [[0, W]];
    if (S >= W) {
      const g = [];
      for (let i = 0; i < W; i++) {
        if (g.length && !_hasSpokenContent(words[i])) g[g.length - 1][1] = i + 1;
        else g.push([i, i + 1]);
      }
      return g;
    }
    const totalW = weights.reduce((a, b) => a + b, 0);
    const totalD = segments.reduce((a, s) => a + (s[1] - s[0]), 0);
    const prefix = [0];
    for (const w of weights) prefix.push(prefix[prefix.length - 1] + w);
    const segFrac = segments.map((s, i) => {
      const df = (s[1] - s[0]) / totalD;
      const ef = segEnergyFrac ? segEnergyFrac[i] : df;
      return (1 - P_EBLEND) * df + P_EBLEND * ef;
    });

    // Punctuationâgap coupling: a break is rewarded in proportion to
    // (strength of trailing punctuation) Ã (relative duration of the silence
    // gap it lands in). A sentence-ending "." breaking into the longest
    // pause is strong evidence; the same "." breaking into a 100ms blip is
    // weak. This anchors the partition to real prosodic pauses and stops
    // cumulative weight-estimation drift from shifting words by a segment.
    const punctStrength = w => {
      if (/[.!?؟]$/.test(w)) return 1.0;
      if (/[,;:،؛…—–-]$/.test(w)) return 0.5;
      return P_DEF; // small default: any word CAN precede a pause
    };
    const gaps = [];
    for (let s = 1; s < S; s++) gaps.push(Math.max(0, segments[s][0] - segments[s - 1][1]));
    const totalGap = gaps.reduce((a, b) => a + b, 0) || 1;
    // LAMBDA sets the balance of prosody prior vs duration matching.
    // Speaking rate varies Â±30% between segments (isolated words are spoken
    // slower than flowing runs), making duration fit noisy â punctuation
    // landing in a long pause is the more reliable signal and must be able
    // to override a one-word duration preference.
    const breakBonus = (idx, sIdx) => {
      if (idx <= 0 || idx >= W || sIdx >= S - 1) return 0;
      const w = words[idx - 1];
      const gapFrac = gaps[sIdx] / totalGap;
      if (/[.!?؟,;:،؛…—–-]$/.test(w)) return P_LAMBDA * punctStrength(w) * gapFrac;
      if (_isFunctionWord(w)) return -P_LAMBDA * P_FUNC * gapFrac;
      return P_LAMBDA * P_DEF * gapFrac;
    };

    // A voiced segment is, by definition, speech â a group containing only
    // punctuation tokens can never legitimately own one.
    const spokenPrefix = [0];
    for (const w of words)
      spokenPrefix.push(spokenPrefix[spokenPrefix.length - 1] + (_hasSpokenContent(w) ? 1 : 0));

    const INF = Infinity;
    const dp = Array.from({ length: S + 1 }, () => new Float64Array(W + 1).fill(INF));
    const back = Array.from({ length: S + 1 }, () => new Int32Array(W + 1).fill(-1));
    dp[0][0] = 0;
    for (let s = 1; s <= S; s++) {
      for (let i = s; i <= W - (S - s); i++) {
        for (let j = s - 1; j < i; j++) {
          if (dp[s - 1][j] === INF) continue;
          if (spokenPrefix[i] - spokenPrefix[j] === 0) continue; // no speech in group
          const frac = (prefix[i] - prefix[j]) / totalW;
          const cost = dp[s - 1][j]
                     + Math.pow(frac - segFrac[s - 1], 2)
                     - breakBonus(i, s - 1);
          if (cost < dp[s][i]) { dp[s][i] = cost; back[s][i] = j; }
        }
      }
    }
    const groups = [];
    let i = W;
    for (let s = S; s >= 1; s--) {
      const j = back[s][i];
      groups.unshift([j, i]);
      i = j;
    }
    return groups;
  }

  function _buildWordEntry(word, start, end) {
    const chars = [];
    const cw = [...word].map(charWeight);
    const cwTotal = cw.reduce((a, b) => a + b, 0) || 1;
    let t = start;
    [...word].forEach((ch, ci) => {
      const cd = ((cw[ci] || 0.001) / cwTotal) * (end - start);
      chars.push({ char: ch, start_time: _r(t), end_time: _r(t + cd) });
      t += cd;
    });
    return { word, start_time: _r(start), end_time: _r(end), chars };
  }

  function _ulawToLinear(u) {
    u = ~u & 0xFF;
    const sign = u & 0x80 ? -1 : 1;
    const exponent = (u >> 4) & 0x07;
    const mantissa = u & 0x0F;
    return sign * (((mantissa << 3) + 0x84) << exponent) - sign * 0x84;
  }

  function _r(x) { return Math.round(x * 1000) / 1000; }


  /* ======================= StreamingAligner ======================= */
  /**
   * Real-time alignment for streamed TTS audio (e.g. base64 PCM chunks
   * arriving over NDJSON). Strategy: incremental re-alignment â the full
   * transcript is known upfront (it's TTS), alignment runs ~1000Ã faster
   * than real time, so every arriving chunk triggers a cheap re-alignment
   * of all audio so far. Words become STABLE once a silence gap is
   * observed after them; frontier words are provisional and may shift
   * until the next pause is seen. Playback trails generation, so by the
   * time the playhead reaches a word it has virtually always stabilized.
   *
   *   const s = new StreamingAligner({ transcript, sampleRate: 24000 });
   *   s.onUpdate = ({ words, stableUntil, revision }) => { ... };
   *   ndjsonLine => s.pushBase64(JSON.parse(line).audio);
   *   ...stream ends: const final = s.finalize();
   */
  class StreamingAligner {
    constructor({
      transcript,
      sampleRate = 24000,
      encoding = "pcm16",        // pcm16 | float32 | ulaw (raw, headerless)
      updateEveryMs = 200,        // re-align at most this often (audio time)
      stableGapMs = 250,          // pause length that "closes" a region
      alignerOptions = {},
    } = {}) {
      if (!transcript) throw new Error("StreamingAligner requires the transcript.");
      this.transcript = transcript;
      this.sampleRate = sampleRate;
      this.encoding = encoding;
      this.updateEverySamples = Math.round((updateEveryMs / 1000) * sampleRate);
      this.stableGap = stableGapMs / 1000;
      this.aligner = new AudioAligner(alignerOptions);
      this.chunks = [];
      this.total = 0;
      this._sinceAlign = 0;
      this.revision = 0;
      this.result = null;
      this.stableUntil = 0;
      this.onUpdate = null;
      this._finalized = false;
      this._frozenWords = [];
      this._frozenTime = 0;
      this._remainingWords = transcript.trim().split(/\s+/);
      this._tailWords = null;
      this._tailSegs = null;
      this._rate = 8.5;   // weight/sec prior; self-calibrates from frozen regions
      this._calW = 0;
      this._calD = 0;
      this._pendingFreeze = null;
    }

    /** Feed a base64 string of raw PCM (per constructor encoding). */
    pushBase64(b64) {
      const raw = (b64.includes(",") ? b64.split(",")[1] : b64).replace(/\s+/g, "");
      const bytes = _b64ToBytes(raw);
      this.push(AudioAligner.decodeRawPCM(bytes, this.encoding));
    }

    /** Feed a mono Float32Array chunk directly. */
    push(float32) {
      if (this._finalized) throw new Error("StreamingAligner already finalized.");
      this.chunks.push(float32);
      this.total += float32.length;
      this._sinceAlign += float32.length;
      if (this._sinceAlign >= this.updateEverySamples) this._realign();
    }

    _pcm() {
      const out = new Float32Array(this.total);
      let off = 0;
      for (const c of this.chunks) { out.set(c, off); off += c.length; }
      // collapse into single chunk to avoid repeated copies as stream grows
      this.chunks = [out];
      return out;
    }

    _realign() {
      this._sinceAlign = 0;
      const pcm = this._pcm();
      if (pcm.length < this.sampleRate * 0.2) return;
      if (!this._remainingWords.length) return this._emit(pcm.length);

      // Only align the words this audio could plausibly contain: aligning
      // the FULL remaining transcript against partial audio would cram
      // unspoken words into observed segments (and freezing would pin the
      // error). Estimate capacity from observed voiced duration Ã speaking
      // rate; the rate self-calibrates from frozen regions as we go.
      const preSegs = this.aligner._voicedSegments(pcm, this.sampleRate);
      const voicedDur = preSegs.reduce((a, s) => a + (s[1] - s[0]), 0);
      const targetWeight = voicedDur * this._rate; // exact take: surplus words distort the DP
      let cum = 0, take = 0;
      while (take < this._remainingWords.length &&
             (cum < targetWeight || take < preSegs.length)) {
        cum += wordWeight(this._remainingWords[take]);
        take++;
      }
      const windowWords = this._remainingWords.slice(0, Math.max(take, 1));
      const res = this.aligner.alignPCM(pcm, this.sampleRate, windowWords.join(" "));

      // offset window-relative times to absolute stream time
      const off = this._frozenTime;
      res.words.forEach(w => {
        w.start_time = _r(w.start_time + off);
        w.end_time = _r(w.end_time + off);
        w.chars.forEach(c => { c.start_time = _r(c.start_time + off); c.end_time = _r(c.end_time + off); });
        w.stable = false;
      });
      const segs = res.voiced_segments.map(s => ({ start_time: s.start_time + off, end_time: s.end_time + off }));

      // FREEZE: pin everything up to the second-to-last CLOSED segment
      // (one-segment lag lets the DP settle before decisions become final).
      // Frozen words never move again; their audio leaves the buffer, so
      // each re-alignment costs only the active tail â O(n) overall.
      const closed = [];
      for (let i = 0; i < segs.length - 1; i++) {
        if (segs[i + 1].start_time - segs[i].end_time >= this.stableGap) closed.push(i);
      }
      if (closed.length >= 2) {
        const fi = closed[closed.length - 2];
        const freezeSegEnd = segs[fi].end_time;
        const freezeAt = (segs[fi].end_time + segs[fi + 1].start_time) / 2; // mid-gap
        const keep = [], freeze = [];
        res.words.forEach(w => (w.end_time <= freezeSegEnd + 1e-6 ? freeze : keep).push(w));
        // DOUBLE-CONFIRMATION GATE: a block may freeze only if two
        // consecutive re-alignments (with fresh audio in between) produced
        // the same words at the same times. Over-cramming â words squeezed
        // into observed segments before their own audio has arrived â is
        // inherently unstable: the next chunk moves them. Requiring the
        // candidate to survive one more alignment makes cram structurally
        // unfreezable, with no circular rate assumptions.
        // ...and confirmation only counts once the segment AFTER the freeze
        // point has substantial observed audio (>=0.35s): the DP must have
        // had a real alternative landing zone and still kept these words
        // where they are. Two looks at the same silence prove nothing.
        const nextSegObserved = (segs[fi + 1].end_time - segs[fi + 1].start_time) >= 0.35;
        let confirmed = false;
        if (nextSegObserved && this._pendingFreeze &&
            this._pendingFreeze.length === freeze.length &&
            freeze.every((w, i) => Math.abs(w.start_time - this._pendingFreeze[i]) < 0.08)) {
          confirmed = true;
        }
        if (nextSegObserved) this._pendingFreeze = freeze.map(w => w.start_time);
        let candW = 0, candD = 0;
        freeze.forEach(w => { candW += wordWeight(w.word); });
        for (let i = 0; i <= fi; i++) candD += segs[i].end_time - segs[i].start_time;
        if (confirmed && freeze.length && keep.length) { // never freeze everything mid-stream
          freeze.forEach(w => { w.stable = true; this._frozenWords.push(w); });
          this._remainingWords = this._remainingWords.slice(freeze.length);
          // self-calibrate speaking rate from frozen (settled) regions
          this._calD += candD; this._calW += candW;
          if (this._calD > 1.5) this._rate = this._calW / this._calD;
          const cut = Math.round((freezeAt - off) * this.sampleRate);
          this.chunks = [pcm.slice(cut)];
          this.total = this.chunks[0].length;
          this._frozenTime = freezeAt;
          this._pendingFreeze = null;
          res.words.length = 0;
          res.words.push(...keep);
        }
      }
      this._tailWords = res.words;
      this._tailSegs = segs;
      return this._emit(pcm.length);
    }

    _emit(windowSamples) {
      this.revision++;
      const words = [...this._frozenWords, ...(this._tailWords || [])];
      this.stableUntil = this._frozenWords.length
        ? this._frozenWords[this._frozenWords.length - 1].end_time : 0;
      this.result = { words, duration: _r(this._frozenTime + windowSamples / this.sampleRate) };
      if (this.onUpdate) this.onUpdate({
        words,
        stableUntil: this.stableUntil,
        revision: this.revision,
        audioDuration: this.result.duration,
      });
      return this.result;
    }

    /** Word under the playhead at time t (seconds). Binary search. */
    wordAt(t) {
      const ws = this.result ? this.result.words : null;
      if (!ws) return null;
      let lo = 0, hi = ws.length - 1;
      while (lo <= hi) {
        const mid = (lo + hi) >> 1;
        if (t < ws[mid].start_time) hi = mid - 1;
        else if (t >= ws[mid].end_time) lo = mid + 1;
        else return ws[mid];
      }
      return null;
    }

    /** Stream ended: final alignment; everything becomes stable. */
    finalize() {
      const pcm = this._pcm();
      if (this._remainingWords.length && pcm.length > this.sampleRate * 0.05) {
        const res = this.aligner.alignPCM(pcm, this.sampleRate, this._remainingWords.join(" "));
        const off = this._frozenTime;
        res.words.forEach(w => {
          w.start_time = _r(w.start_time + off);
          w.end_time = _r(w.end_time + off);
          w.chars.forEach(c => { c.start_time = _r(c.start_time + off); c.end_time = _r(c.end_time + off); });
        });
        this._tailWords = res.words;
      }
      const words = [...this._frozenWords, ...(this._tailWords || [])];
      words.forEach(w => { w.stable = true; });
      const duration = _r(this._frozenTime + pcm.length / this.sampleRate);
      const result = { duration, sampleRate: this.sampleRate, words };
      const el = AudioAligner.toElevenLabs(result, this.transcript);
      result.alignment = el.alignment;
      result.normalized_alignment = el.normalized_alignment;
      this.result = result;
      this.stableUntil = duration;
      this._finalized = true;
      return result;
    }
  }

  /* ============================ export ============================ */

  if (typeof module !== "undefined" && module.exports) {
    module.exports = { AudioAligner, StreamingAligner };
  } else {
    global.AudioAligner = AudioAligner;
    global.StreamingAligner = StreamingAligner;
  }
})(typeof window !== "undefined" ? window : {});
